// ./src/components/features/MarketingChannels/MarketingChannelSelect/MarketingChannelSelect.tsx
//
// Picks one of the account's marketing channels for an EVENT, with in-place create — the entity-picker pattern of
// `VolunteerRoleSelect` / `ArtistSelect`. Used by the share panel (#138) to tag a link with a channel's `?ch=`.
//
// ⚠ FULL SCREEN ON A PHONE. With a handful of channels SelectField's old auto rule kept the inline dropdown, and
//   inside the Share drawer's bottom sheet that dropdown resized the sheet on every open and scroll — it flickered
//   (owner, 2026-10-09). SelectField now opens the full-screen picker on every phone (`check-mobile-pickers`).
// ⚠ It OWNS its provider, scoped by `eventId` (crew with `event.operate` read and create in the EVENT's account), and
//   its read: the list is loaded HERE and its failure is that READ's result — not the context's `lastError`, which a
//   failed CREATE in the drawer sets too (a plan refusal there must not read as "could not load the channels").
// ⚠ "Loaded" is the HOOK's flag, not a local one: the hook EMPTIES its list when the signed-in user changes (and drops a
//   read that a newer one superseded), so a local "done" said "No channels yet — add one" over channels that exist —
//   found in the browser. Whenever the hook is not loaded and no read of ours is in flight, the picker reads again.
// ⚠ A failed read shows an alert with a retry INSTEAD of the picker: an empty picker over a refused read looks like
//   "no channels yet", and its create action would add a duplicate of one that exists.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Loader, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { MarketingChannelsProvider, useMarketingChannelsContext } from '@/contexts';
import { useCreateInPlace } from '@/hooks/useCreateInPlace';
import { CREATE_FOR_EVENT_OPERATORS } from '@/utils/createInPlace';
import { AppIcon, SelectField } from '@/components/generic';
import { DRAWER_COMBOBOX_PROPS } from '@/utils';
import { MarketingChannel, WHOLE_SET } from '@/utils/api';

import { MarketingChannelEditDrawer } from '../MarketingChannelEditDrawer/MarketingChannelEditDrawer';

export interface MarketingChannelSelectProps {
  /** The event the channel is picked FOR — the auth context of the read and of a create. */
  eventId: string;
  /** The picked channel's id (null = none). */
  value: string | null;
  /** The picked channel (null when cleared). */
  onChange: (channel: MarketingChannel | null) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  placeholder?: string;
  /**
   * Render nothing when the account has no channels AND may not create one (a plan without them) — for a surface where
   * the picker is an extra, like the share panel. Omitted → the picker shows, saying why it is empty.
   */
  hideWhenUnusable?: boolean;
}

const ChannelSelectInner: React.FC<MarketingChannelSelectProps> = ({ eventId, value, onChange, label, description, placeholder, hideWhenUnusable }) => {
  const { t } = useTranslation(['marketingChannels', 'common']);
  const { marketingChannels, list, isLoaded } = useMarketingChannelsContext();
  // #142 — a crew member only with `event.operate`. ⓘ No plan: channels are on every plan since 2026-10-22 (owner).
  const canCreate = useCreateInPlace(CREATE_FOR_EVENT_OPERATORS, null, eventId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // What was typed in the picker's search becomes the new channel's name.
  const [initialName, setInitialName] = useState('');
  const [inFlight, setInFlight] = useState(false);
  const [failed, setFailed] = useState(false);
  // ⚠ Only the LATEST read settles the state (#138 review): the hook still returns a superseded read's result, and
  //   letting it clear `inFlight` (or set `failed`) started reads over a read still running, or showed "could not load"
  //   for a read nobody is waiting on.
  const loadIdRef = useRef(0);

  const load = useCallback(async () => {
    const loadId = ++loadIdRef.current;

    setInFlight(true);
    setFailed(false);
    const result = await list({}, { page: 1, pageSize: WHOLE_SET });

    if (loadId !== loadIdRef.current) {
      return;
    }

    setFailed(!result.didSucceed());
    setInFlight(false);
  }, [list]);

  useEffect(() => {
    if (!isLoaded && !inFlight && !failed) {
      void load();
    }
  }, [isLoaded, inFlight, failed, load]);

  const read: 'loading' | 'failed' | 'ready' = failed ? 'failed' : isLoaded && !inFlight ? 'ready' : 'loading';

  const channels = useMemo(() => marketingChannels.filter((c) => !!c.id && !!c.code), [marketingChannels]);
  const data = useMemo(() => channels.map((c) => ({ value: c.id as string, label: c.name || (c.code as string) })), [channels]);

  const startCreate = (search: string) => {
    setInitialName(search.trim());
    setDrawerOpen(true);
  };

  const handleCreated = (channel: MarketingChannel) => {
    setDrawerOpen(false);

    if (channel.id && channel.code) {
      onChange(channel);
    }
  };

  if (read === 'failed') {
    return (
      <Alert variant="light" color="yellow" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
        <Stack gap="xs" align="flex-start">
          <Text size="md" ta="left">
            {t('marketingChannels:select.readFailed', {
              defaultValue: 'Could not load the marketing channels, so this link is not tagged with one. The plain link below still works.',
            })}
          </Text>
          <Button variant="light" size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={() => void load()}>
            {t('common:retry', { defaultValue: 'Try again' })}
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (hideWhenUnusable && read === 'ready' && channels.length === 0 && !canCreate) {
    return null;
  }

  return (
    <>
      <SelectField
        name="channelId"
        label={label}
        description={description}
        placeholder={read === 'loading' ? t('common:loading', { defaultValue: 'Loading…' }) : placeholder}
        data={data}
        value={value}
        onChange={(_n, id) => onChange(channels.find((c) => c.id === id) ?? null)}
        createLabel={canCreate ? t('marketingChannels:select.create', { defaultValue: 'Add a new channel' }) : undefined}
        onCreate={canCreate ? (search) => startCreate(search) : undefined}
        searchable
        clearable
        disabled={read === 'loading'}
        nothingFoundMessage={t('marketingChannels:select.empty', { defaultValue: 'No channels yet — add one' })}
        leftSection={<AppIcon iconName="Speakerphone" size={16} />}
        rightSection={read === 'loading' ? <Loader size="sm" /> : undefined}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
        size="md"
      />

      <MarketingChannelEditDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} recordId={null} initialName={initialName || null} onCreated={handleCreated} />
    </>
  );
};

export const MarketingChannelSelect: React.FC<MarketingChannelSelectProps> = (props) => (
  // autoLoad off: the picker runs its own read, so its failure is that read's result (see the header).
  // ⚠ Keyed by the event: with autoLoad off the hook does not re-list on a new event, so a new event is a new picker.
  <MarketingChannelsProvider key={props.eventId} pageSize={WHOLE_SET} eventId={props.eventId} autoLoad={false}>
    <ChannelSelectInner {...props} />
  </MarketingChannelsProvider>
);
